import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ExperienceCard from "@/components/ExperienceCard";
import PillarBadge from "@/components/PillarBadge";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Compass, Sunrise, Users, Sprout } from "lucide-react";

const HERO_IMG = "/images/hero-cabin.jpg";

const PILLARS_MEDIA = [
  {
    key: "spirituality",
    title: "Yoga",
    tagline: "Asana · Pranayama · Meditation",
    body: "Return to breath, to stillness, to a slower kind of time. Not doctrine — self-awareness.",
    img: "https://images.pexels.com/photos/35960251/pexels-photo-35960251.jpeg",
    icon: Sunrise,
  },
  {
    key: "work",
    title: "Working",
    tagline: "Nomad · Farm · Craft · Founder",
    body: "Ship your project from a rice terrace. Learn organic farming. Build with strangers becoming friends.",
    img: "https://images.pexels.com/photos/5992574/pexels-photo-5992574.jpeg",
    icon: Sprout,
  },
  {
    key: "activities",
    title: "Learning",
    tagline: "Cycling · Rivers · Birds · Music",
    body: "The stuff that makes memory. Villages by bicycle, rivers by boat, birds at dawn, kirtan at dusk.",
    img: "https://images.unsplash.com/photo-1759147806909-970d2258a50c",
    icon: Compass,
  },
  {
    key: "sharing",
    title: "Sharing",
    tagline: "Skill-share · Work-exchange · Collab",
    body: "Bring what you know, take what you learn. Pottery, code, music, farming — all valid currencies.",
    img: "https://images.pexels.com/photos/18373966/pexels-photo-18373966.jpeg",
    icon: Users,
  },
];

const PERSONAS = [
  { slug: "yoga", label: "Yoga Seekers" },
  { slug: "remote-work", label: "Remote Workers" },
  { slug: "digital-nomad", label: "Digital Nomads" },
  { slug: "artist", label: "Artists and Makers" },
  { slug: "creative", label: "Content Creators" },
  { slug: "birder", label: "Birders and Naturalists" },
  { slug: "founder", label: "Entrepreneurs" },
];

export default function Landing() {
  const [experiences, setExperiences] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/experiences?limit=6").then((r) => setExperiences(r.data)).catch(() => setExperiences([]));
  }, []);

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-16 grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7 animate-fade-up">
            <div className="overline mb-6">Retreat Travel · Live • Work • Explore • Share</div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-[5.5rem] tracking-tight leading-[0.95] font-light">
              Travel that <span className="italic text-moss">transforms</span> lives.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-muted2 leading-relaxed">
              Travel beyond sightseeing. Discover handpicked retreats where you can practice yoga, focus on your work, explore local life, and share meaningful experiences with like-minded people and communities. LIVE, WORK, EXPLORE and SHARE, all in one place.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button data-testid="hero-cta-discover" onClick={() => navigate("/discover")} className="rounded-full bg-moss hover:bg-moss-hover text-canvas px-7 py-6 text-base">
                Discover experiences <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
              <Link data-testid="hero-cta-host" to="/for-hosts" className="text-sm underline underline-offset-4 hover:text-moss">
                or list your sanctuary →
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <div className="aspect-[4/5] overflow-hidden rounded-2xl grain-overlay">
              <img src={HERO_IMG} alt="Rustic jungle lodge" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-canvas border border-subtle rounded-2xl p-5 shadow-sm max-w-[220px]">
              <div className="overline mb-1">This week</div>
              <div className="font-serif text-2xl leading-tight">42 hosts across 9 countries</div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS - Tetris grid */}
      <section id="pillars" className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid md:grid-cols-12 gap-8 mb-14">
          <div className="md:col-span-5">
            <div className="overline mb-4">Four pillars, one journey</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">
              Not a checklist. A <span className="italic">weaving</span>.
            </h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 text-muted2 leading-relaxed">
            Every Retreat Travel experience is woven around some combination of these four pillars. Mornings begin with yoga, days move into your own work, afternoons open into activities shaped for you, and evenings wind down in sharing — around a fire, a meal, or an idea.
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-6">
          {PILLARS_MEDIA.map((p, i) => {
            const Icon = p.icon;
            const spanClass = i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : i === 2 ? "md:col-span-5" : "md:col-span-7";
            const heightClass = i === 0 || i === 3 ? "aspect-[16/10]" : "aspect-[4/5]";
            return (
              <Link
                key={p.key}
                data-testid={`pillar-tile-${p.key}`}
                to={`/discover?pillar=${p.key}`}
                className={`group relative overflow-hidden rounded-2xl ${spanClass} ${heightClass} block animate-fade-up`}
              >
                <img src={p.img} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute inset-0 p-8 flex flex-col justify-end text-canvas">
                  <Icon className="h-6 w-6 mb-3 opacity-80" strokeWidth={1.5} />
                  <div className="overline text-canvas/80">{p.tagline}</div>
                  <div className="font-serif text-4xl md:text-5xl mt-1 leading-none">{p.title}</div>
                  <p className="mt-3 max-w-md text-sm text-canvas/85 leading-relaxed">{p.body}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* PERSONAS */}
      <section className="bg-[#F4F2EC] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="overline mb-8">Who this is for</div>
          <div className="flex flex-wrap gap-3">
            {PERSONAS.map((p) => (
              <button
                key={p.slug}
                data-testid={`persona-chip-${p.slug}`}
                onClick={() => navigate(`/discover?persona=${p.slug}`)}
                className="rounded-full bg-white border border-subtle px-5 py-2.5 text-sm hover:border-moss hover:text-moss transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED EXPERIENCES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline mb-3">Curated this month</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Journeys ready to receive you</h2>
          </div>
          <Link data-testid="view-all-experiences" to="/discover" className="hidden md:inline-flex items-center gap-1 text-sm text-moss hover:underline">
            View all <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {experiences.map((e) => <ExperienceCard key={e.id} exp={e} />)}
        </div>
      </section>

      {/* SHARING/CTA */}
      <section id="about" className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="rounded-2xl bg-moss text-canvas p-10 md:p-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="overline text-canvas/70">For hosts</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight mt-2">
              You built something rare. Let travelers who care actually find it.
            </h2>
            <p className="mt-4 text-canvas/85 max-w-xl">
              Ashrams, farmstays, artist collectives, wildlife lodges — if your space combines any two of our four pillars, Retreat Travel is your marketplace.
            </p>
          </div>
          <div className="md:col-span-5 flex md:justify-end">
            <Button data-testid="footer-cta-host" onClick={() => navigate("/register")} className="rounded-full bg-canvas text-moss hover:bg-canvas/90 px-7 py-6">
              List your space <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
